import { v1 as uuidv1 } from 'uuid';
import { specialDecimalOffsets, standardDecimalOffset } from './data/currency';
let eventCount = 0;
/**
 * Returns and bumps the current event count. Plug this into the
 * 'eventCount' event property.
 */
export function bumpEventCount() {
    const c = eventCount;
    eventCount += 1;
    return c;
}
/**
 * Returns the current event count, but does not bump. Only use
 * this if you're interested in the current event count for
 * for debug purposes.
 */
export function getEventCountNoBump() {
    return eventCount;
}
/**
 * Returns number of seconds since 'ECMAScript epoch', ready to be plugged
 * into the Client Timestamp event property.
 */
export function getClientTimestamp() {
    return Math.round(new Date().getTime() / 1000);
}
/**
 * Returns a reasonably 'collision-safe' session id string using a UUIDv1
 * (normally, a V1 UUID uses the MAC address and timestamp to generate a
 * unique value, but since the MAC address isn't accessible from browsers,
 * 48 bits of randomness are used instead
 * (see: https://github.com/uuidjs/uuid/blob/4de23a6030e65ac72b3b015680f08e7e292681ed/src/v1.js#L25-L41)
 */
export function getSessionId() {
    return uuidv1();
}
/**
 * A helper function to extract an Ad Failed error message string from the catch-error
 * object in a try-catch block. Tries to detect what sort of error object it
 * is (for instance from FBInstant's AdInstance.showAsync()). If the
 * error object can't be identified, the function will return null.
 *
 * @param catchError an error object from a try-catch block
 * @returns an Ad Response reason string (e.g. 'USER_INPUT') or null
 */
export function extractAdResponseErrorMessage(catchError) {
    const hasCodeProperty = (obj) => typeof obj.code === 'string';
    let reason = null;
    if (catchError !== undefined && catchError !== null) {
        if (hasCodeProperty(catchError)) {
            reason = catchError.code;
        }
    }
    return reason;
}
// Precompile regex used in parsePriceTag
const priceChunksRegex = /(\d+)/g;
/**
 * A helper function that parses a price tag string and returns its numeric value in cents.
 *
 * @param {string} priceTag - The price tag string to parse.
 * @param {SupportedCurrencies} currency - The currency used in the price tag.
 * @returns {number | null} The numeric value of the price tag in cents or null if parsing failed.
 */
export function parsePriceTag(priceTag, currency) {
    var _a;
    const chunks = priceTag.match(priceChunksRegex);
    if (!chunks || chunks.length === 0) {
        return null;
    }
    let integer;
    let fraction;
    if (chunks.length === 1) {
        // Only integer part was provided
        integer = chunks[0];
        fraction = '00';
    }
    else {
        integer = chunks.slice(0, -1).join('');
        fraction = chunks.slice(-1).join('');
    }
    // Calculate numeric value in cents
    const decimalOffset = (_a = specialDecimalOffsets[currency]) !== null && _a !== void 0 ? _a : standardDecimalOffset;
    const numberValue = parseFloat(`${integer}.${fraction}`) * decimalOffset;
    // Check if the native conversion to float actually worked
    if (Number.isNaN(numberValue)) {
        return null;
    }
    // Round number to avoid floating point innacuracies
    const roundedResult = Math.round(numberValue);
    if (priceTag.includes('-')) {
        return -roundedResult;
    }
    return roundedResult;
}
/**
 * A helper function that encodes the user email string and returns its hash value.
 *
 * @param {string} userEmail - The user email string to encode.
 * @returns {number | null} The numeric value of the SHA-256 digest or null in case it failed to convert.
 */
export async function userEmailEncode(userEmail) {
    if (userEmail.trim().length === 0) {
        return null;
    }
    const encoder = new TextEncoder();
    try {
        const data = encoder.encode(userEmail);
        const hash = await crypto.subtle.digest('SHA-256', data);
        return Array.prototype.map
            .call(new Uint8Array(hash), (x) => `00${x.toString(16)}`.slice(-2)) // to ensure that each string has exactly two digits by adding leading zeros if necessary.
            .join(''); // concatenates all the strings in the array into a single string.
    }
    catch (e) {
        return null;
    }
}
//# sourceMappingURL=eventutil.js.map